import React, { useState, useCallback, useEffect } from "react";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
  Chip,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import style from "./table.module.css";
import moment from "moment";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const getColor = (value, clickable) => {
  const colorMap = {
    active: "green",
    inactive: "#dade17",
    approved: "green",
    accepted: "green",
    completed: "green",
    rejected: "red",
    success: "green",
    successful: "green",
    pending: "#d2d200",
    inprogress: "#d2d200",
    created: "grey",
    available: "green",
    activated: "blue",
    retired: "orange",
    terminated: "red",
    suspended: "red",
  };

  const val =
    typeof value === "string" && value.trim().length > 0
      ? value.toLowerCase()
      : "";

  return colorMap[val] || (clickable ? "#418fe1" : "black");
};

const getStatusColor = (status) => {
  switch (status?.toUpperCase?.()) {
    case "CREATED":
      return "rgb(225 225 225)";
    case "APPROVED":
      return "#95e198";
    case "REJECTED":
      return "#da857f";
    case "PENDING":
      return "#fff59d";
    default:
      return "";
  }
};

const sortDataByDate = (data) => {
  if (!data || data.length === 0) return data;

  // Make a copy to avoid modifying the original
  const dataCopy = [...data];

  // Check for common date fields
  const sampleItem = dataCopy[0];
  const dateField =
    "createdAt" in sampleItem
      ? "createdAt"
      : "submittedAt" in sampleItem
        ? "submittedAt"
        : "timestamp" in sampleItem
          ? "timestamp"
          : "date" in sampleItem
            ? "date"
            : null;

  if (!dateField) return dataCopy; // No date field found

  // Sort by the identified date field (newest first)
  return dataCopy.sort((a, b) => {
    const valA = a[dateField] || "";
    const valB = b[dateField] || "";
    return String(valB).localeCompare(String(valA), undefined, {
      numeric: true,
    });
  });
};

const TableWrapper = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(2),
  overflow: "hidden",
}));

const TableHeadFixed = styled(TableHead)(({ theme }) => ({
  position: "sticky",
  top: 0,
  backgroundColor: "#fff",
  zIndex: 1,
}));

const ScrollableTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "37.5rem", //this set table height
  padding: "0px 4px",
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "10px",
    border: "2px solid transparent",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f1f1f1",
    borderRadius: "10px",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "#888 #f1f1f1",
}));
const CommonTable = ({
  columns,
  data,
  setData,
  filters,
  totalRows,
  onPageChange,
  onRowsPerPageChange,
  tableCss,
  handleGetById,
  iscard,
  onRowClick,
  showsort,
  onToggleExpand,
}) => {
  const serialColumnIndex = columns.findIndex((col) => col.key === "serial");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [hasManualSort, setHasManualSort] = useState(false);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedData = [...data].sort((a, b) => {
      const valA = a[key] || "";
      const valB = b[key] || "";

      return direction === "asc"
        ? String(valA).localeCompare(String(valB), undefined, { numeric: true })
        : String(valB).localeCompare(String(valA), undefined, {
            numeric: true,
          });
    });

    setData(sortedData);
    setSortConfig({ key, direction });
    setHasManualSort(true);
  };

  // Fixed: Handle individual checkbox change using unique identifier (iccid)
  const handleIndividualCheckboxChange = useCallback(
    (iccid, checked) => {
      const updated = data.map((item) =>
        item.iccid === iccid ? { ...item, checked } : item,
      );
      setData(updated);
    },
    [data, setData],
  );

  // Handle select all checkbox change
  const handleSelectAllChange = useCallback(
    (checked) => {
      const updated = data.map((item) => ({
        ...item,
        checked,
      }));
      setData(updated);
    },
    [data, setData],
  );
  const allChecked = data?.length > 0 && data?.every((item) => item?.checked);
  const someChecked = data?.some((item) => item?.checked) && !allChecked;
  // const displayData = data;

  // *------------------  if needed data in sorted manner like newly created appear on top then  *---------//
  const displayData = React.useMemo(() => {
    // If user has manually sorted, respect that choice
    if (hasManualSort) {
      return data;
    }

    // Keep grouped hierarchical rows in their provided order
    if (data?.some((row) => row?.isChild || row?.hasChildren)) {
      return data;
    }

    // Otherwise apply automatic date sorting
    return sortDataByDate(data);
  }, [data, hasManualSort]);

  // *------------------------------------------------------*------------------------//
  return (
    <TableWrapper
      className={style.commonTableContainer}
      sx={{ width: tableCss.tableWidth || "95%!important" }}
    >
      <ScrollableTableContainer>
        <Table sx={{ minWidth: 300 }} aria-label="common-table">
          <TableHeadFixed>
            <TableRow className={style.commonStylingForHeader}>
              {serialColumnIndex !== -1 && (
                <TableCell
                  align="center"
                  style={{
                    borderRadius: "14px 0px 0px 14px",
                    borderBottom: "none",
                    color: "white",
                    minWidth: 50,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {columns[serialColumnIndex]?.showcheck && (
                    <Checkbox
                      checked={allChecked}
                      indeterminate={someChecked}
                      onChange={(e) => handleSelectAllChange(e.target.checked)}
                      sx={{
                        color: "white",
                        "&.Mui-checked": { color: "white" },
                      }}
                    />
                  )}
                  Sr. No
                </TableCell>
              )}

              {columns.map(
                (column, index) =>
                  column.key !== "serial" &&
                  column.key !== "selectAll" &&
                  column?.showTable && (
                    <TableCell
                      key={column.key}
                      align={column.align || "left"}
                      style={{
                        color: "white",
                        minWidth: column.minWidth || 100,
                        borderBottom: "none",
                        borderRadius:
                          index === columns.length - 1
                            ? "0px 14px 14px 0px"
                            : undefined,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {column.name}
                        {showsort && (
                          <span
                            onClick={() => handleSort(column.key)}
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              userSelect: "none",
                              padding: "8px 12px",
                              borderRadius: "8px",
                              transition: "all 0.2s ease",
                              background:
                                sortConfig.key === column.key
                                  ? "rgba(0, 0, 0, 0.08)"
                                  : "transparent",
                              color: "inherit",
                              fontWeight:
                                sortConfig.key === column.key ? "600" : "500",
                              fontSize: "14px",
                              boxShadow:
                                sortConfig.key === column.key
                                  ? "0 2px 8px rgba(0, 0, 0, 0.1)"
                                  : "none",
                              transform: "translateY(0)",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = "translateY(-2px)";
                              e.target.style.boxShadow =
                                sortConfig.key === column.key
                                  ? "0 4px 12px rgba(0, 0, 0, 0.15)"
                                  : "0 2px 8px rgba(0, 0, 0, 0.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow =
                                sortConfig.key === column.key
                                  ? "0 2px 8px rgba(0, 0, 0, 0.1)"
                                  : "none";
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "4px",
                                borderRadius: "50%",
                                background: "rgba(0, 0, 0, 0.05)",
                                backdropFilter: "blur(10px)",
                                transition: "all 0.2s ease",
                              }}
                            >
                              {sortConfig.key === column.key ? (
                                sortConfig.direction === "asc" ? (
                                  <ArrowUpwardIcon
                                    style={{
                                      fontSize: "16px",
                                      filter:
                                        "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                                    }}
                                  />
                                ) : (
                                  <ArrowDownwardIcon
                                    style={{
                                      fontSize: "16px",
                                      filter:
                                        "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                                    }}
                                  />
                                )
                              ) : (
                                <ArrowUpwardIcon
                                  style={{
                                    fontSize: "16px",
                                    opacity: 0.6,
                                    filter:
                                      "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
                                  }}
                                />
                              )}
                            </div>
                          </span>
                        )}
                      </div>
                    </TableCell>
                  ),
              )}
            </TableRow>
          </TableHeadFixed>
          <TableBody>
            {displayData?.length > 0 ? (
              displayData.map((row, displayIndex) => (
                <React.Fragment
                  key={`${row.iccid || displayIndex}-${displayIndex}`}
                >
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    sx={{
                      background: row?.isChild
                        ? "#f8fbff"
                        : getStatusColor(row?.kycStatus),
                      borderRadius: "14px",
                      boxShadow: "0px 0px 3px 0px grey",
                      borderLeft: row?.isChild ? "4px solid #dbe8ff" : "none",
                      cursor: iscard ? "pointer" : "default",
                      "&:hover": {
                        boxShadow: "0px 0px 1px 2px #709ed3",
                        backgroundColor: iscard
                          ? "#c5d9f1"
                          : row?.isChild
                            ? "#eef5ff"
                            : "#f9f9f9",
                      },
                    }}
                    onClick={(e) => {
                      if (
                        iscard &&
                        typeof onRowClick === "function" &&
                        !e.target.closest(".MuiCheckbox-root") &&
                        !e.target.closest(".MuiIconButton-root")
                      ) {
                        onRowClick(row);
                      }
                    }}
                  >
                    {serialColumnIndex !== -1 && (
                      <TableCell
                        align="center"
                        sx={{
                          borderRadius: "14px 0px 0px 14px",
                          borderBottom: "none",
                          padding: tableCss.cellPadding,
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          minWidth: 110,
                        }}
                      >
                        {row?.hasChildren ? (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (typeof onToggleExpand === "function") {
                                onToggleExpand(
                                  row.parentBatchNumber || row.batchNumber,
                                );
                              }
                            }}
                            sx={{ padding: 0.5 }}
                          >
                            {row?.isExpanded ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </IconButton>
                        ) : (
                          <Box sx={{ width: 32, flexShrink: 0 }} />
                        )}

                        {columns[serialColumnIndex]?.showcheck && (
                          <Checkbox
                            checked={row?.checked || false}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleIndividualCheckboxChange(
                                row.iccid,
                                e.target.checked,
                              );
                            }}
                          />
                        )}
                        {/* Calculate correct serial number for normal display */}
                        {row.displaySerial ??
                          (filters?.pageNo
                            ? filters.pageNo * filters.pageSize +
                              displayIndex +
                              1
                            : displayIndex + 1)}
                      </TableCell>
                    )}

                    {columns.map(
                      (column, colIndex) =>
                        column.key !== "serial" &&
                        column.key !== "selectAll" &&
                        column?.showTable && (
                          <TableCell
                            key={column.key}
                            align={column.align || "left"}
                            sx={{
                              cursor: column.clickable ? "pointer" : "default",
                              borderBottom: "none",
                              padding: tableCss.cellPadding,
                              paddingLeft:
                                row?.isChild && column.key === "batchNumber"
                                  ? "28px"
                                  : undefined,
                              borderRadius:
                                colIndex === columns.length - 1
                                  ? "0px 14px 14px 0px"
                                  : undefined,
                              color: getColor(
                                row[column.key],
                                column.clickable,
                              ),
                              visibility:
                                row.status === "SUCCESSFUL" &&
                                column.key === "action"
                                  ? "hidden"
                                  : "visible",
                            }}
                            onClick={(e) => {
                              if (
                                column.clickable &&
                                typeof handleGetById === "function"
                              ) {
                                handleGetById(row, displayIndex);
                              }
                            }}
                          >
                            {/* Cell content remains the same */}
                            {Array.isArray(row[column.key]) ? (
                              column.key === "allocatedTos" ? (
                                <Tooltip title={row[column.key].join(", ")}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    {row[column.key]
                                      .slice(0, 2)
                                      .map((account, index) => (
                                        <Chip
                                          key={index}
                                          label={account}
                                          size="small"
                                          color="primary"
                                        />
                                      ))}
                                    {row[column.key].length > 2 && (
                                      <Chip
                                        label={`+${row[column.key].length - 2}`}
                                        size="small"
                                      />
                                    )}
                                  </Box>
                                </Tooltip>
                              ) : (
                                <>
                                  {row[column.key][0]}
                                  {row[column.key].length > 1 && (
                                    <span> +{row[column.key].length - 1}</span>
                                  )}
                                </>
                              )
                            ) : column?.date ? (
                              row[column.key] ? (
                                moment(row[column.key]).format("DD-MM-YYYY")
                              ) : (
                                "N/A"
                              )
                            ) : row[column.key] === 0 ? (
                              "0"
                            ) : (
                              row[column.key] || "-"
                            )}
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                  <div style={{ margin: "10px 0px" }}></div>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={columns.length + 1}
                  sx={{
                    borderRadius: "14px",
                    boxShadow: "0px 0px 3px 0px grey",
                    borderBottom: "0px solid rgba(224, 224, 224, 1)!important",
                  }}
                >
                  No Data To Show!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ScrollableTableContainer>
      {totalRows > 10 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, totalRows < 50 ? 100 : totalRows]}
          component="div"
          count={totalRows || 0}
          rowsPerPage={filters?.pageSize}
          page={filters?.pageNo}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </TableWrapper>
  );
};

export default CommonTable;
